/**
 * @fileoverview Upcoming Assignments List Component
 * @description Displays doctor's upcoming assignments in filterable table format
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - Mantine Table with WCAG 2.1 AA accessibility
 */

import { useState } from 'react';
import {
  Table,
  Card,
  Text,
  Group,
  Badge,
  TextInput,
  Button,
  Stack,
  Paper,
  ScrollArea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconFilter,
  IconCalendar,
  IconBuilding,
  IconClock,
  IconX,
} from '@tabler/icons-react';
import { useUpcomingAssignments, extractAssignments } from '../../../hooks/doctor/useDoctorSchedule';
import type { DoctorAssignment, UpcomingAssignmentsFilters } from '../../../types/doctor';

/**
 * Props for UpcomingAssignmentsList component
 */
export interface UpcomingAssignmentsListProps {
  /** Optional custom class name */
  className?: string;
  
  /** Optional maximum height for scrolling */
  maxHeight?: number;
  
  /** Show filters (default: true) */
  showFilters?: boolean;
}

/**
 * Upcoming Assignments List Component
 * @description Displays filterable table of doctor's upcoming assignments
 * 
 * @example
 * ```tsx
 * function DoctorDashboard() {
 *   return (
 *     <div>
 *       <DoctorKPICards kpis={kpis} />
 *       <UpcomingAssignmentsList maxHeight={400} />
 *     </div>
 *   );
 * }
 * ```
 */
export function UpcomingAssignmentsList({
  className,
  maxHeight = 500,
  showFilters = true,
}: UpcomingAssignmentsListProps) {
  // Filter state
  const [filters, setFilters] = useState<UpcomingAssignmentsFilters>({});
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  // Fetch assignments with filters
  const assignmentsQuery = useUpcomingAssignments(filters);
  const assignments = extractAssignments(assignmentsQuery);

  /**
   * Handle filter changes
   */
  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    setDateRange(range);
    setFilters((prev) => {
      const newFilters: UpcomingAssignmentsFilters = { ...prev };
      if (range[0]) {
        newFilters.startDate = range[0].toISOString().split('T')[0];
      } else {
        delete newFilters.startDate;
      }
      if (range[1]) {
        newFilters.endDate = range[1].toISOString().split('T')[0];
      } else {
        delete newFilters.endDate;
      }
      return newFilters;
    });
  };

  const handleSessionLabelChange = (value: string | null) => {
    setFilters((prev) => {
      const newFilters: UpcomingAssignmentsFilters = { ...prev };
      if (value) {
        newFilters.sessionLabel = value;
      } else {
        delete newFilters.sessionLabel;
      }
      return newFilters;
    });
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setFilters({});
  };

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder {...(className && { className })}>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Text fw={600} size="lg">
              Upcoming Assignments
            </Text>
            <Text size="sm" c="dimmed">
              {assignments.length} {assignments.length === 1 ? 'session' : 'sessions'} scheduled
            </Text>
          </div>
          
          {hasActiveFilters && (
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              leftSection={<IconX size={14} />}
              onClick={handleClearFilters}
              aria-label="Clear all filters"
            >
              Clear Filters
            </Button>
          )}
        </Group>

        {/* Filters */}
        {showFilters && (
          <Paper p="md" radius="md" withBorder bg="gray.0">
            <Group gap="md" align="flex-end">
              <DatePickerInput
                type="range"
                label="Date Range"
                placeholder="Select date range"
                value={dateRange}
                onChange={handleDateRangeChange}
                leftSection={<IconCalendar size={16} />}
                clearable
                style={{ flex: 1, minWidth: 200 }}
                aria-label="Filter by date range"
              />

              <TextInput
                label="Session Label"
                placeholder="e.g., Morning, Afternoon"
                value={filters.sessionLabel || ''}
                onChange={(e) => handleSessionLabelChange(e.currentTarget.value || null)}
                leftSection={<IconClock size={16} />}
                style={{ flex: 1, minWidth: 150 }}
                aria-label="Filter by session label"
              />

              <Button
                variant="light"
                leftSection={<IconFilter size={16} />}
                disabled={!hasActiveFilters}
                aria-label="Active filters applied"
              >
                {hasActiveFilters ? `${Object.keys(filters).length} Active` : 'No Filters'}
              </Button>
            </Group>
          </Paper>
        )}

        {/* Table */}
        <ScrollArea h={maxHeight} type="auto">
          {assignmentsQuery.isLoading ? (
            <AssignmentsTableSkeleton />
          ) : assignmentsQuery.error ? (
            <Paper p="xl" radius="md" withBorder bg="red.0">
              <Text c="red" ta="center">
                Failed to load assignments. Please try again.
              </Text>
            </Paper>
          ) : assignments.length === 0 ? (
            <Paper p="xl" radius="md" withBorder bg="gray.0">
              <Text c="dimmed" ta="center">
                {hasActiveFilters 
                  ? 'No assignments match the selected filters.' 
                  : 'No assignments yet. Published schedules will appear here.'}
              </Text>
            </Paper>
          ) : (
            <Table
              striped
              highlightOnHover
              verticalSpacing="sm"
              horizontalSpacing="md"
              role="table"
              aria-label="Upcoming assignments table"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Session</Table.Th>
                  <Table.Th>Time</Table.Th>
                  <Table.Th>Room</Table.Th>
                  <Table.Th>Building</Table.Th>
                  <Table.Th>Floor</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {assignments.map((assignment) => (
                  <AssignmentRow key={assignment.id} assignment={assignment} />
                ))}
              </Table.Tbody>
            </Table>
          )}
        </ScrollArea>
      </Stack>
    </Card>
  );
}

/**
 * Assignment table row component
 * @param assignment - Assignment data
 */
function AssignmentRow({ assignment }: { assignment: DoctorAssignment }) {
  const date = new Date(assignment.date);
  const isToday = isDateToday(date);
  const isTomorrow = isDateTomorrow(date);

  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </Text>
          {isToday && (
            <Badge size="xs" color="blue" variant="light">
              Today
            </Badge>
          )}
          {isTomorrow && (
            <Badge size="xs" color="cyan" variant="light">
              Tomorrow
            </Badge>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={500}>
          {assignment.sessionTime.name}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {assignment.sessionTime.startTime} - {assignment.sessionTime.endTime}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {assignment.room.number} - {assignment.room.name}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={4}>
          <IconBuilding size={14} stroke={1.5} style={{ opacity: 0.6 }} aria-hidden="true" />
          <Text size="sm">{assignment.room.floor.building.name}</Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {assignment.room.floor.name}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge
          color={getStatusColor(assignment.status)}
          variant="light"
          size="sm"
        >
          {assignment.status}
        </Badge>
      </Table.Td>
    </Table.Tr>
  );
}

/**
 * Skeleton loader for assignments table
 */
function AssignmentsTableSkeleton() {
  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Date</Table.Th>
          <Table.Th>Session</Table.Th>
          <Table.Th>Time</Table.Th>
          <Table.Th>Room</Table.Th>
          <Table.Th>Building</Table.Th>
          <Table.Th>Floor</Table.Th>
          <Table.Th>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {[...Array(5)].map((_, i) => (
          <Table.Tr key={i}>
            <Table.Td>
              <div style={{ height: 20, width: 100, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            </Table.Td>
            <Table.Td>
              <div style={{ height: 20, width: 120, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            </Table.Td>
            <Table.Td>
              <div style={{ height: 20, width: 80, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            </Table.Td>
            <Table.Td>
              <div style={{ height: 20, width: 100, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            </Table.Td>
            <Table.Td>
              <div style={{ height: 20, width: 100, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            </Table.Td>
            <Table.Td>
              <div style={{ height: 20, width: 60, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            </Table.Td>
            <Table.Td>
              <div style={{ height: 20, width: 80, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

/**
 * Get badge color for assignment status
 * @param status - Assignment status
 * @returns Mantine color name
 */
function getStatusColor(status: string): string {
  switch (status.toUpperCase()) {
    case 'CONFIRMED':
      return 'green';
    case 'PENDING':
      return 'yellow';
    case 'CANCELLED':
      return 'red';
    case 'COMPLETED':
      return 'gray';
    default:
      return 'blue';
  }
}

/**
 * Check if date is today
 * @param date - Date to check
 * @returns True if date is today
 */
function isDateToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

/**
 * Check if date is tomorrow
 * @param date - Date to check
 * @returns True if date is tomorrow
 */
function isDateTomorrow(date: Date): boolean {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return (
    date.getFullYear() === tomorrow.getFullYear() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getDate() === tomorrow.getDate()
  );
}

